import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";
import {
  type ApolloClient,
  type DocumentNode,
  type OperationVariables,
  type TypedDocumentNode,
} from "@apollo/client";
import { useApolloClient } from "@apollo/client/react";
import { Kind, OperationTypeNode } from "graphql";

import { FORBIDDEN_CODES } from "#app/lib/utils/is-forbidden-error";

// ---------------------------------------------------------------------------
// The app's ONE home for GraphQL subscriptions.
//
// SubscriptionProvider owns every live wire subscription in a ref-counted
// registry keyed by (operation, variables): the first view interested in a
// key opens the WebSocket operation, later views attach to it, and the last
// one leaving tears it down. Events fan out to every registered handler, so
// two surfaces watching the same events (e.g. the board and the list on one
// workflow) share a single wire subscription while reacting independently.
//
// Handlers write to the Apollo cache (or refetch queries) — the cache stays
// the data store; this registry deliberately holds NO app state of its own.
//
// Consumers declare interest with the useRegisterSubscriptions hook, passing
// descriptors built by a domain's factory (e.g.
// kanbanBoardSubscriptions(workflowId)).
// ---------------------------------------------------------------------------

export interface SubscriptionRequest<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
> {
  document: TypedDocumentNode<TData, TVariables> | DocumentNode;
  variables?: TVariables;
  /** Fired per event. Write to the cache / refetch — don't hold state here. */
  onData: (client: ApolloClient, data: TData) => void;
}

/** Registration-side erasure — what registries/views accept. Descriptor
 *  factories stay fully typed; the registry only needs the common shape. */
export type AnySubscriptionRequest = SubscriptionRequest<any, any>;

/**
 * Identity helper for descriptor factories: infers TData/TVariables from the
 * codegen document so `onData`'s payload is fully typed, then erases to the
 * registration shape. Without it, an `AnySubscriptionRequest[]` literal types
 * every handler's `data` as `any`.
 */
export function defineSubscription<
  TData,
  TVariables extends OperationVariables,
>(request: {
  document: TypedDocumentNode<TData, TVariables>;
  variables?: TVariables;
  onData: (client: ApolloClient, data: TData) => void;
}): AnySubscriptionRequest {
  return request;
}

type Handler = (client: ApolloClient, data: unknown) => void;

interface RegistryEntry {
  handlers: Set<Handler>;
  unsubscribe: () => void;
  /** Re-open the wire operation now (used when a new consumer joins a key
   *  that has stopped retrying). */
  restart: () => void;
  retryTimer: ReturnType<typeof setTimeout> | null;
  /** Consecutive re-open attempts since the last delivered event. */
  attempt: number;
  /** True once retrying stopped (attempts exhausted, or a permanent
   *  rejection). A newly registering handler gets one fresh attempt series. */
  stopped: boolean;
}

type Register = (
  key: string,
  request: AnySubscriptionRequest,
  handler: Handler,
) => () => void;

const SubscriptionRegistryContext = createContext<Register | null>(null);

// A wire subscription killed by an operation error retries while anyone is
// still registered — presence/board subs are long-lived and a transient
// server hiccup shouldn't silently freeze them until remount. (Socket-level
// drops are already retried inside graphql-ws and never surface here.)
//
// Retries back off and eventually give up, because not every rejection is
// transient: subscriptions with an object-level subscribe gate reject
// permanently for the current session (activityChanged answers NOT_FOUND once
// the viewer loses visibility on the issue). A fixed 5s loop for as long as
// the component stays mounted would hammer the API forever over a rejection
// that can never change.
const RETRY_BASE_MS = 5_000;
const RETRY_MAX_MS = 60_000;
const RETRY_MAX_ATTEMPTS = 6;

// Codes that describe the REQUEST, not the server's health — re-sending the
// identical operation cannot change the answer. FORBIDDEN_CODES is the shared
// auth-deny list (see is-forbidden-error.ts); NOT_FOUND covers the
// visibility-denial spelling that hides a subject's existence; the rest mean
// the document or its arguments are wrong.
//
// UNAUTHENTICATED is deliberately NOT here: the WS path bypasses the error
// link's sign-out, and connectionParams re-resolve the token on reconnect, so
// an expired-token subscribe is worth backing off through rather than giving
// up on immediately.
const NON_RETRYABLE_CODES = new Set<string>([
  ...FORBIDDEN_CODES,
  "NOT_FOUND",
  "BAD_USER_INPUT",
  "GRAPHQL_VALIDATION_FAILED",
  "GRAPHQL_PARSE_FAILED",
]);

/** GraphQL errors carried by a subscription failure. GraphQLWsLink wraps an
 *  operation error as an ApolloError; the raw array shape is accepted too so
 *  classification doesn't hinge on that wrapping. Socket-level failures carry
 *  none and stay retryable. */
function graphQLErrorsOf(err: unknown): readonly unknown[] {
  if (Array.isArray(err)) return err;
  const errors = (err as { graphQLErrors?: readonly unknown[] } | null)
    ?.graphQLErrors;
  return Array.isArray(errors) ? errors : [];
}

function isPermanentRejection(err: unknown): boolean {
  return graphQLErrorsOf(err).some((e) => {
    const code = (e as { extensions?: { code?: unknown } })?.extensions?.code;
    return typeof code === "string" && NON_RETRYABLE_CODES.has(code);
  });
}

// Documents are module constants, so the AST walk runs once per operation
// for the app's lifetime instead of per descriptor per render.
const operationNameCache = new WeakMap<DocumentNode, string>();

function operationName(document: DocumentNode): string {
  let name = operationNameCache.get(document);
  if (name === undefined) {
    const op = document.definitions.find(
      (d) =>
        d.kind === Kind.OPERATION_DEFINITION &&
        d.operation === OperationTypeNode.SUBSCRIPTION,
    );
    name =
      (op?.kind === Kind.OPERATION_DEFINITION ? op.name?.value : undefined) ??
      "anonymous";
    operationNameCache.set(document, name);
  }
  return name;
}

/** Stable identity for one wire subscription: operation + variables. */
export function subscriptionKey(
  request: Pick<SubscriptionRequest, "document" | "variables">,
): string {
  return `${operationName(request.document)}:${JSON.stringify(request.variables ?? {})}`;
}

export function SubscriptionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const client = useApolloClient();
  const entriesRef = useRef(new Map<string, RegistryEntry>());

  const register = useCallback<Register>(
    (key, request, handler) => {
      const entries = entriesRef.current;
      let entry = entries.get(key);
      if (!entry) {
        const created: RegistryEntry = {
          handlers: new Set(),
          unsubscribe: () => {},
          restart: () => {},
          retryTimer: null,
          attempt: 0,
          stopped: false,
        };
        // Re-open the operation if anyone still cares, backing off so a
        // server that stays broken isn't hammered, and stopping outright once
        // attempts are exhausted.
        const scheduleRetry = () => {
          if (created.handlers.size === 0) return;
          if (created.attempt >= RETRY_MAX_ATTEMPTS) {
            created.stopped = true;
            console.warn(
              `[subscriptions] ${key} gave up after ${RETRY_MAX_ATTEMPTS} attempts`,
            );
            return;
          }
          const delay = Math.min(
            RETRY_BASE_MS * 2 ** created.attempt,
            RETRY_MAX_MS,
          );
          created.attempt += 1;
          console.warn(
            `[subscriptions] ${key} reopening in ${delay / 1000}s (attempt ${created.attempt}/${RETRY_MAX_ATTEMPTS})`,
          );
          created.retryTimer = setTimeout(() => {
            created.retryTimer = null;
            if (created.handlers.size > 0) start();
          }, delay);
        };
        const start = () => {
          created.stopped = false;
          const observable = client.subscribe({
            query: request.document,
            variables: request.variables,
          });
          const sub = observable.subscribe({
            next: (result) => {
              if (!result.data) return;
              // A delivered event proves the wire is healthy — the next
              // failure starts its backoff from scratch.
              created.attempt = 0;
              for (const h of created.handlers) {
                h(client, result.data);
              }
            },
            error: (err: unknown) => {
              console.warn(
                `[subscriptions] ${key} errored:`,
                err instanceof Error ? err.message : err,
              );
              // Lost visibility, denied role, malformed document: asking
              // again with the same operation gets the same answer.
              if (isPermanentRejection(err)) {
                created.stopped = true;
                console.warn(
                  `[subscriptions] ${key} rejection is permanent, not retrying`,
                );
                return;
              }
              scheduleRetry();
            },
            complete: () => {
              // A clean server-side complete on a sub someone still wants is
              // unexpected (these are open-ended event streams) — surface it
              // and re-open rather than going silently deaf.
              if (created.handlers.size === 0) return;
              console.warn(`[subscriptions] ${key} completed unexpectedly, reopening`);
              scheduleRetry();
            },
          });
          created.unsubscribe = () => sub.unsubscribe();
        };
        created.restart = start;
        start();
        entries.set(key, created);
        entry = created;
      } else if (entry.stopped && !entry.retryTimer) {
        // Shared key that has stopped retrying: a newly interested consumer
        // gets one fresh attempt series rather than attaching to a dead wire
        // (whatever denied it — a permission change, a server fault — may
        // have resolved since).
        entry.attempt = 0;
        entry.unsubscribe();
        entry.restart();
      }
      entry.handlers.add(handler);

      return () => {
        const current = entries.get(key);
        if (!current) return;
        current.handlers.delete(handler);
        if (current.handlers.size === 0) {
          if (current.retryTimer) clearTimeout(current.retryTimer);
          current.unsubscribe();
          entries.delete(key);
        }
      };
    },
    [client],
  );

  // Tear down every remaining wire subscription if the provider itself
  // unmounts (client swap on logout).
  useEffect(() => {
    const entries = entriesRef.current;
    return () => {
      for (const entry of entries.values()) {
        if (entry.retryTimer) clearTimeout(entry.retryTimer);
        entry.unsubscribe();
      }
      entries.clear();
    };
  }, [client]);

  return (
    <SubscriptionRegistryContext.Provider value={register}>
      {children}
    </SubscriptionRegistryContext.Provider>
  );
}

/**
 * Register a set of subscription descriptors for as long as the caller is
 * mounted. Wire subscriptions are shared by key; handlers always see the
 * latest closure (safe to build descriptors inline each render). Pass an
 * empty array to register nothing (the "skip" idiom).
 */
export function useRegisterSubscriptions(
  requests: AnySubscriptionRequest[],
): void {
  const register = useContext(SubscriptionRegistryContext);
  if (!register) {
    throw new Error(
      "useRegisterSubscriptions must be used within SubscriptionProvider",
    );
  }

  // Latest descriptors by key — the registered handler proxies through this
  // so re-renders refresh closures without re-subscribing the wire.
  const requestsByKeyRef = useRef(new Map<string, AnySubscriptionRequest>());
  const byKey = new Map<string, AnySubscriptionRequest>();
  for (const request of requests) byKey.set(subscriptionKey(request), request);
  requestsByKeyRef.current = byKey;

  const keys = [...byKey.keys()].sort().join("|");

  useEffect(() => {
    const unregisters = [...requestsByKeyRef.current.entries()].map(
      ([key, request]) =>
        register(key, request, (client, data) => {
          const latest = requestsByKeyRef.current.get(key);
          (latest ?? request).onData(client, data);
        }),
    );
    return () => {
      for (const unregister of unregisters) unregister();
    };
    // `keys` stands in for the request list — same keys ⇒ same wire subs.
  }, [register, keys]);
}
